/**
 * VoiceRecorder — in-browser audio recorder with waveform visualization.
 *
 * Features:
 * - Large record button with pulse animation while recording
 * - Live waveform visualisation during recording
 * - Timer showing recording duration
 * - Playback with waveform scrubber
 * - Re-record option
 * - File size and duration indicator
 *
 * The recorder produces audio only: the recorded Blob is handed to the
 * parent through onRecordingComplete (null when the recording is deleted or
 * re-recorded). Nothing is transcribed or summarised here — the Whisper +
 * Claude chain that once ran after recording was removed on 29 September
 * 2026 (client files carry the adviser's written Client File Brief instead;
 * prep requests and referrals attach the audio as-is).
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, Play, Pause, RotateCcw, Trash2 } from 'lucide-react';

/**
 * Format seconds as MM:SS display string.
 */
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format bytes to a human-readable size.
 */
function formatSize(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function VoiceRecorder({ onRecordingComplete, disabled = false }) {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  // Playback state
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Waveform data
  const [waveformData, setWaveformData] = useState([]);
  const [playbackWaveform, setPlaybackWaveform] = useState([]);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const animationRef = useRef(null);
  const audioRef = useRef(null);

  /**
   * Stop recording and clean up resources.
   */
  const stopRecording = useCallback((cleanup = false) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (cleanup) {
      setIsRecording(false);
      setIsPaused(false);
    }
  }, []);

  /**
   * Draw live waveform visualization during recording.
   */
  const drawWaveform = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      analyser.getByteFrequencyData(dataArray);

      // Sample a few frequency bins for the waveform bar display
      const barCount = 40;
      const step = Math.floor(bufferLength / barCount);
      const bars = [];
      for (let i = 0; i < barCount; i++) {
        const value = dataArray[i * step] / 255;
        bars.push(value);
      }

      setWaveformData(bars);
      animationRef.current = requestAnimationFrame(draw);
    };

    draw();
  }, []);

  /**
   * Generate a static waveform from the recorded blob for playback display.
   */
  const generatePlaybackWaveform = useCallback(async (blob) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const arrayBuffer = await blob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const channelData = audioBuffer.getChannelData(0);

      const barCount = 60;
      const step = Math.floor(channelData.length / barCount);
      const bars = [];

      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        for (let j = 0; j < step; j++) {
          sum += Math.abs(channelData[i * step + j]);
        }
        bars.push(sum / step);
      }

      // Normalize
      const max = Math.max(...bars, 0.01);
      setPlaybackWaveform(bars.map((v) => v / max));
      setDuration(audioBuffer.duration);

      audioContext.close();
    } catch (err) {
      console.error('Failed to generate waveform:', err);
      // Fallback: generate random-looking waveform
      setPlaybackWaveform(Array.from({ length: 60 }, () => Math.random() * 0.5 + 0.2));
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const currentAudioUrl = audioUrl;
    return () => {
      stopRecording(true);
      if (currentAudioUrl) URL.revokeObjectURL(currentAudioUrl);
    };
  }, [audioUrl, stopRecording]);

  /**
   * Start recording audio from the user's microphone.
   */
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;

      // Set up audio analysis for waveform
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Start MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);

        setAudioBlob(blob);
        setAudioUrl(url);
        setIsRecording(false);
        setIsPaused(false);

        // Notify parent component
        if (onRecordingComplete) {
          onRecordingComplete(blob);
        }

        // Generate playback waveform from recorded data
        generatePlaybackWaveform(blob);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(100); // Collect data every 100ms

      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setWaveformData([]);
      setAudioBlob(null);
      setAudioUrl(null);

      // Start recording timer
      const startTime = Date.now();
      timerRef.current = setInterval(() => {
        setRecordingTime((Date.now() - startTime) / 1000);
      }, 100);

      // Start waveform animation
      drawWaveform();
    } catch (err) {
      console.error('Microphone access denied:', err);
      alert('Please allow microphone access to record a voice note.');
    }
  }, [onRecordingComplete, drawWaveform, generatePlaybackWaveform]);

  /**
   * Toggle audio playback.
   */
  const togglePlayback = () => {
    if (!audioRef.current) {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.addEventListener('timeupdate', () => {
        setPlaybackTime(audio.currentTime);
      });

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setPlaybackTime(0);
      });

      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  /**
   * Re-record — discard current recording and start fresh.
   */
  const reRecord = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);

    setAudioBlob(null);
    setAudioUrl(null);
    setIsPlaying(false);
    setPlaybackTime(0);
    setDuration(0);
    setWaveformData([]);
    setPlaybackWaveform([]);
    setRecordingTime(0);

    if (onRecordingComplete) {
      onRecordingComplete(null);
    }
  };

  /**
   * Delete the recording entirely.
   */
  const deleteRecording = () => {
    reRecord();
  };

  // Calculate playback progress as percentage
  const playbackProgress = duration > 0 ? (playbackTime / duration) * 100 : 0;

  return (
    <div style={styles.container}>
      <AnimatePresence mode="wait">
        {/* State 1: Ready to record (no recording yet) */}
        {!isRecording && !audioBlob && (
          <motion.div
            key="ready"
            style={styles.readyState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.button
              type="button"
              style={styles.recordBtn}
              onClick={startRecording}
              disabled={disabled}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Mic size={28} />
            </motion.button>
            <p style={styles.instructions}>Tap to start recording your voice note</p>
          </motion.div>
        )}

        {/* State 2: Currently recording */}
        {isRecording && (
          <motion.div
            key="recording"
            style={styles.recordingState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Recording pulse indicator */}
            <div style={styles.recordingHeader}>
              <motion.div
                style={styles.recordingDot}
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              />
              <span style={styles.recordingLabel}>Recording</span>
              <span style={styles.timer}>{formatTime(recordingTime)}</span>
            </div>

            {/* Live waveform */}
            <div style={styles.waveformContainer}>
              {waveformData.map((value, index) => (
                <motion.div
                  key={index}
                  style={{
                    ...styles.waveformBar,
                    height: `${Math.max(4, value * 48)}px`,
                    background: `rgba(15, 76, 117, ${0.3 + value * 0.7})`,
                  }}
                  animate={{ height: `${Math.max(4, value * 48)}px` }}
                  transition={{ duration: 0.05 }}
                />
              ))}
              {/* Fill remaining bars if waveform hasn't started */}
              {waveformData.length === 0 &&
                Array.from({ length: 40 }, (_, i) => (
                  <div
                    key={i}
                    style={{ ...styles.waveformBar, height: '4px', background: 'rgba(15, 76, 117, 0.2)' }}
                  />
                ))}
            </div>

            {/* Stop button */}
            <motion.button
              type="button"
              style={styles.stopBtn}
              onClick={() => stopRecording()}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Square size={20} fill="white" />
              <span>Stop Recording</span>
            </motion.button>
          </motion.div>
        )}

        {/* State 3: Recording complete — playback mode */}
        {!isRecording && audioBlob && (
          <motion.div
            key="playback"
            style={styles.playbackState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Playback waveform with progress overlay */}
            <div style={styles.playbackRow}>
              <motion.button
                type="button"
                style={styles.playBtn}
                onClick={togglePlayback}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isPlaying ? <Pause size={18} /> : <Play size={18} />}
              </motion.button>

              <div style={styles.playbackWaveform}>
                {playbackWaveform.map((value, index) => {
                  const barProgress = (index / playbackWaveform.length) * 100;
                  const isPlayed = barProgress < playbackProgress;
                  return (
                    <div
                      key={index}
                      style={{
                        ...styles.playbackBar,
                        height: `${Math.max(4, value * 36)}px`,
                        background: isPlayed ? '#0F4C75' : '#D0DAE6',
                      }}
                    />
                  );
                })}
              </div>

              <span style={styles.playbackTime}>
                {formatTime(isPlaying ? playbackTime : duration)}
              </span>
            </div>

            {/* File info + action buttons */}
            <div style={styles.playbackFooter}>
              <span style={styles.fileInfo}>
                {formatSize(audioBlob.size)} · {formatTime(duration)}
              </span>
              <div style={styles.playbackActions}>
                <button
                  type="button"
                  style={styles.actionBtn}
                  onClick={reRecord}
                  title="Re-record"
                >
                  <RotateCcw size={14} />
                  Re-record
                </button>
                <button
                  type="button"
                  style={{ ...styles.actionBtn, color: '#C0392B' }}
                  onClick={deleteRecording}
                  title="Delete recording"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const styles = {
  container: {
    border: '1.5px solid #D0DAE6',
    borderRadius: '12px',
    padding: '1.25rem',
    background: '#FAFCFE',
  },
  // Ready state
  readyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem 0',
  },
  recordBtn: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #0F4C75, var(--cp-secondary))',
    color: 'white',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(15, 76, 117, 0.3)',
    transition: 'all 0.2s',
  },
  instructions: {
    fontSize: '0.85rem',
    color: '#6B7A8D',
  },
  // Recording state
  recordingState: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  recordingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  recordingDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#C0392B',
    flexShrink: 0,
  },
  recordingLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#C0392B',
    flex: 1,
  },
  timer: {
    fontFamily: "'DM Sans', monospace",
    fontSize: '1.1rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    fontVariantNumeric: 'tabular-nums',
  },
  waveformContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    height: '52px',
    padding: '0 0.5rem',
  },
  waveformBar: {
    width: '3px',
    borderRadius: '2px',
    transition: 'height 0.05s ease',
    flexShrink: 0,
  },
  stopBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.65rem 1.25rem',
    background: '#C0392B',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
  },
  // Playback state
  playbackState: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  playbackRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  playBtn: {
    width: '42px',
    height: '42px',
    borderRadius: '50%',
    background: '#0F4C75',
    color: 'white',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  playbackWaveform: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5px',
    flex: 1,
    height: '40px',
  },
  playbackBar: {
    flex: 1,
    minWidth: '2px',
    borderRadius: '1px',
    transition: 'background 0.15s ease',
  },
  playbackTime: {
    fontFamily: "'DM Sans', monospace",
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#6B7A8D',
    minWidth: '42px',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  playbackFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fileInfo: {
    fontSize: '0.75rem',
    color: '#6B7A8D',
  },
  playbackActions: {
    display: 'flex',
    gap: '0.5rem',
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.3rem 0.6rem',
    border: 'none',
    background: 'transparent',
    color: '#6B7A8D',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.15s',
  },
};
